import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { Badge, Box, Fab, Tooltip } from "@mui/material";
import {
  gradientShimmer,
  REDUCED_MOTION_SHIMMER_FILTER,
  voiceClusterMotionSx,
  voiceClusterPhaseColors,
  voiceClusterPhaseFill,
  voiceClusterPhaseHover,
  voiceClusterReducedMotionSx,
  voiceClusterRing,
  voiceClusterStatusShadow,
  voiceStatusBreathe,
  voiceStatusRing,
} from "./aiAssist/animations";
import { EMPTY_STATE_ICON_SRC } from "./drawer/drawerChrome";
import { VOICE_PAUSE_LABEL, VOICE_RESUME_LABEL, type VoiceDialogPhase } from "./voiceLaunch";
import VoicePhaseHint from "./VoicePhaseHint";

const STATUS_ICON_PX = 20;

const statusRingLayer = {
  content: '""',
  position: "absolute",
  inset: 0,
  zIndex: 0,
  borderRadius: "50%",
  pointerEvents: "none",
  opacity: 0,
} as const;

export const TOWER_ASSISTANT_FAB_SIZE_PX = 52;

/**
 * Floating "Open Tower Assistant" control — 52px brand asset with the landing
 * design's shadow and unread badge chrome.
 */
export default function TowerAssistantFab({
  unreadCount = 0,
  onClick,
  "aria-label": ariaLabel = "Open Tower Assistant",
  dialog = null,
}: Readonly<{
  unreadCount?: number;
  onClick: () => void;
  "aria-label"?: string;
  /** While a voice dialog runs with the drawer closed, this circle is pause/play. */
  dialog?: Readonly<{
    phase: VoiceDialogPhase;
    paused: boolean;
  }> | null;
}>) {
  if (dialog !== null) {
    const label = dialog.paused ? VOICE_RESUME_LABEL : VOICE_PAUSE_LABEL;
    return (
      <Box sx={{ position: "relative", display: "inline-flex", lineHeight: 0 }}>
        <VoicePhaseHint phase={dialog.phase} paused={dialog.paused} shown />
        <Fab
          aria-label={label}
          aria-pressed={!dialog.paused}
          data-voice-phase={dialog.phase}
          onClick={onClick}
          sx={(theme) => {
            const assist = theme.app.aiAssist;
            const mode = theme.palette.mode;
            const colors = voiceClusterPhaseColors(theme);
            const phase = dialog.phase;
            const fill =
              phase === "idle" ? assist.accentStrong : voiceClusterPhaseFill(colors, phase);
            const hover =
              phase === "idle" ? assist.accentStrong : voiceClusterPhaseHover(colors, phase);
            const shadow = voiceClusterStatusShadow(fill, mode);
            const ringing = !dialog.paused && (phase === "listening" || phase === "speaking");
            const breathing = !dialog.paused && phase === "thinking";
            const ring = voiceClusterRing(fill, mode);
            return {
              width: TOWER_ASSISTANT_FAB_SIZE_PX,
              height: TOWER_ASSISTANT_FAB_SIZE_PX,
              minWidth: TOWER_ASSISTANT_FAB_SIZE_PX,
              minHeight: TOWER_ASSISTANT_FAB_SIZE_PX,
              p: 0,
              position: "relative",
              overflow: "visible",
              border: 0,
              borderRadius: "50%",
              bgcolor: fill,
              backgroundImage: "none",
              boxShadow: shadow,
              color: assist.onAccent,
              ...voiceClusterMotionSx(assist.gradientStart),
              "&:hover": {
                bgcolor: hover,
                backgroundImage: "none",
                boxShadow: shadow,
                color: assist.onAccent,
              },
              "&::before": {
                ...statusRingLayer,
                ...(ringing
                  ? { bgcolor: ring, animation: `${voiceStatusRing} 1.6s ease-out infinite` }
                  : {}),
                ...(breathing
                  ? { bgcolor: fill, animation: `${voiceStatusBreathe} 2.4s ease-in-out infinite` }
                  : {}),
              },
              "&::after": {
                ...statusRingLayer,
                ...(ringing
                  ? {
                      bgcolor: ring,
                      animation: `${voiceStatusRing} 1.6s ease-out infinite`,
                      animationDelay: "0.8s",
                    }
                  : {}),
              },
              ...voiceClusterReducedMotionSx(),
            };
          }}
        >
          {dialog.paused ? (
            <PlayArrowIcon sx={{ position: "relative", zIndex: 1, fontSize: STATUS_ICON_PX }} />
          ) : (
            <PauseIcon sx={{ position: "relative", zIndex: 1, fontSize: STATUS_ICON_PX }} />
          )}
        </Fab>
      </Box>
    );
  }

  return (
    <Tooltip title="Tower Assistant">
      <Badge
        badgeContent={unreadCount}
        overlap="circular"
        invisible={unreadCount <= 0}
        sx={(theme) => ({
          "& .MuiBadge-badge": {
            minWidth: 18,
            height: 18,
            px: 0,
            top: -4,
            right: -4,
            borderRadius: "9px",
            bgcolor: theme.app.aiAssist.fabBadgeBg,
            boxShadow: theme.app.aiAssist.fabBadgeShadow,
            fontFamily: "var(--font-sans)",
            fontWeight: 700,
            fontSize: 8.45,
            lineHeight: "8px",
            color: theme.app.aiAssist.onAccent,
          },
        })}
      >
        <Fab
          aria-label={ariaLabel}
          onClick={onClick}
          sx={(theme) => ({
            width: TOWER_ASSISTANT_FAB_SIZE_PX,
            height: TOWER_ASSISTANT_FAB_SIZE_PX,
            p: 0,
            borderRadius: "26px",
            bgcolor: "transparent",
            boxShadow: theme.app.aiAssist.fabShadow,
            isolation: "isolate",
            overflow: "hidden",
            "&:hover": {
              bgcolor: "transparent",
              boxShadow: theme.app.aiAssist.fabShadowHover,
              "& img": {
                animation: `${gradientShimmer} 1.4s ease-in-out infinite`,
              },
            },
            "@media (prefers-reduced-motion: reduce)": {
              "&:hover img": {
                animation: "none",
                filter: REDUCED_MOTION_SHIMMER_FILTER,
              },
            },
          })}
        >
          <Box
            component="img"
            src={EMPTY_STATE_ICON_SRC}
            alt=""
            aria-hidden
            sx={{
              width: TOWER_ASSISTANT_FAB_SIZE_PX,
              height: TOWER_ASSISTANT_FAB_SIZE_PX,
              display: "block",
              transition: "filter 0.25s ease",
            }}
          />
        </Fab>
      </Badge>
    </Tooltip>
  );
}
